import { LinkButton } from '@euler/app/components/LinkButton';
import { Typography } from '@euler/app/components/typography';
import { OrderSearchResultList } from '@euler/app/flows/search/components/OrderSearchResultList';
import { useOrderSearchContext } from '@euler/app/flows/search/context';
import { useFastOrderSearch } from '@euler/app/flows/search/functions/useFastOrderSearch';
import { useOrderSearchNavigation } from '@euler/app/flows/search/screens';
import { SearchNavHeader } from '@euler/app/flows/task/components/SearchNavHeader';
import { Center } from '@euler/components/Center';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useAdaptiveContainerWidth } from '@euler/functions/useAdaptiveContainerWidth';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { usePersistFn } from '@euler/utils/hooks';
import { BlurView } from 'expo-blur';
import { memo, useCallback, useMemo, useState } from 'react';
import { KeyboardAvoidingView, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export const FastSearchPage = wrapNavigatorScreen(
  () => {
    return <Content />;
  },
  { headerShown: false },
);

const Content = memo(() => {
  const width = useAdaptiveContainerWidth();
  const navigation = useOrderSearchNavigation();

  const [searchText, setSearchText] = useState<string>();
  const [hasSearched, setHasSearched] = useState(false);

  const {
    fetch,
    data,
    status: { hasMore, isLoading, error },
    fetchSinceLastError,
    onScrollToBottom,
  } = useFastOrderSearch();

  const { openOrderSearchResultItem } = useOrderSearchContext();

  const onSearchInputTextChange = usePersistFn((text: string) => {
    setSearchText(text);
    if (hasSearched) {
      setHasSearched(false);
    }
  });

  const onSearch = useCallback(
    (s: string) => {
      const keyword = s.trim();
      setHasSearched(keyword.length > 0);
      void fetch(keyword);
    },
    [fetch],
  );

  const onSearchAdvanced = useCallback(() => {
    navigation.replace('_advancedSearch', {});
  }, [navigation]);

  const styles = useStyles();

  return (
    <KeyboardAvoidingView behavior="padding" style={styles.flexContainer}>
      <BlurView style={styles.flexContainer}>
        <SearchNavHeader
          searchInputEditable
          searchText={searchText}
          searchPlaceholder={'车牌号或关键词'}
          autoCorrect={false}
          autoCapitalize="characters"
          onSearchInputChange={onSearchInputTextChange}
          onSearch={onSearch}
        />
        <OrderSearchResultList
          showFinishDate
          width={width}
          data={data}
          isLoading={isLoading}
          error={error}
          hasMore={hasMore}
          ListEmptyComponent={
            <EmptyPlaceholder
              isLoading={isLoading}
              hasSearched={hasSearched}
              searchText={searchText}
              onSearchAdvanced={onSearchAdvanced}
            />
          }
          onRetry={fetchSinceLastError}
          onItemPress={openOrderSearchResultItem}
          onScrollToBottom={onScrollToBottom}
          css={`
            flex: 1;
          `}
        />
      </BlurView>
    </KeyboardAvoidingView>
  );
});

const EmptyPlaceholder = memo(
  ({
    isLoading,
    searchText,
    hasSearched,
    onSearchAdvanced,
  }: {
    isLoading?: boolean;
    searchText?: string;
    hasSearched?: boolean;
    onSearchAdvanced?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    if (isLoading) return null;
    return (
      <Center
        css={`
          padding: ${rpx(32)}px;
          height: ${rpx(200)}px;
        `}
      >
        <Typography.Text size={rpx(14)} light color="#555" mb={rpx(8)}>
          {searchText?.trim().length && hasSearched
            ? '未找到相关工单? 请检查车牌号输入是否有误'
            : '输入车牌号或关键词快速搜索工单'}
        </Typography.Text>
        <LinkButton
          text="高级搜索 →"
          textStyle={{
            fontSize: rpx(12),
          }}
          onPress={onSearchAdvanced}
        />
      </Center>
    );
  },
);

const useStyles = () => {
  const { rpx } = useResponsiveContext();
  const { bottom } = useSafeAreaInsets();
  return useMemo(
    () =>
      StyleSheet.create({
        flexContainer: {
          flex: 1,
        },
        listContainer: {
          flexGrow: 1,
          minHeight: 0,
          paddingBottom: Math.max(bottom, rpx(15)),
          paddingTop: rpx(10),
          paddingLeft: rpx(15),
          paddingRight: rpx(15),
        },
      }),
    [bottom, rpx],
  );
};
